import React, { useState, useEffect } from "react";
import { Trash2, X, Plus } from "lucide-react";
import FieldConfigurationAPI from "../../../../lib/api/FieldConfiguration";
import SearchableCodeSelect from "../../../common/SearchableCodeSelect";

const EMPLOYEE_OPTION = {
  getOptionKey: (o) => o.uuid || o.code,
  formatOptionLabel: (o) => {
    const name = [o.first_name, o.middle_name, o.last_name]
      .filter(Boolean)
      .join(" ");
    return name ? `${o.code} - ${name}` : o.code;
  },
  renderOption: (o) => (
    <>
      <div className="searchable-code-select__option-code">{o.code}</div>
      <div className="searchable-code-select__option-name">
        {[o.first_name, o.middle_name, o.last_name].filter(Boolean).join(" ")}
      </div>
    </>
  ),
};

function blankRule() {
  return {
    filter_type: "include",
    employees: {
      from: "",
      to: "",
      status: "active",
      from_location: "",
      to_location: "",
    },
  };
}

export default function EmployeesFilterModal({
  open,
  groupUuid,
  companyUuid,
  filter,
  onSave,
  onClose,
  readOnly = false,
}) {
  const [rules, setRules] = useState([blankRule()]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open) return;
    const existing = filter?.rules;
    setRules(
      existing && existing.length > 0
        ? existing.map((r) => ({
            filter_type: r.filter_type || "include",
            employees: {
              from: r.employees?.from ?? "",
              to: r.employees?.to ?? "",
              status: r.employees?.status ?? "active",
              from_location: r.employees?.from_location ?? "",
              to_location: r.employees?.to_location ?? "",
            },
          }))
        : [blankRule()]
    );
    setError(null);
  }, [open, filter]);

  if (!open) return null;

  function updateRule(index, field, value) {
    setRules((prev) =>
      prev.map((r, i) =>
        i === index
          ? { ...r, employees: { ...r.employees, [field]: value } }
          : r
      )
    );
  }

  function updateFilterType(index, value) {
    setRules((prev) =>
      prev.map((r, i) => (i === index ? { ...r, filter_type: value } : r))
    );
  }

  function removeRule(index) {
    setRules((prev) =>
      prev.length === 1 ? [blankRule()] : prev.filter((_, i) => i !== index)
    );
  }

  function handleSave() {
    const cleanedRules = rules.map((r) => ({
      filter_type: r.filter_type,
      employees: {
        from: r.employees.from || null,
        to: r.employees.to || null,
        status: r.employees.status || null,
        from_location: r.employees.from_location || null,
        to_location: r.employees.to_location || null,
      },
    }));

    onSave({ resource: "employees", mode: "filtered", rules: cleanedRules });
  }

  return (
    <div className="jfm-overlay" onClick={onClose}>
      <div className="jfm-container" onClick={(e) => e.stopPropagation()}>
        <div className="jfm-header">
          <span className="jfm-title">Employees Filters</span>
          <button className="jfm-close" onClick={onClose}>
            <X size={18} />
          </button>
        </div>

        <div className="jfm-body">
          <fieldset className="jfm-fieldset" disabled={readOnly}>
            {rules.map((rule, idx) => (
              <div key={idx} className="efm-row">
                <select
                  className="jfm-type-select"
                  value={rule.filter_type}
                  onChange={(e) => updateFilterType(idx, e.target.value)}
                >
                  <option value="include">Include</option>
                  <option value="exclude">Exclude</option>
                </select>

                <div className="efm-fields">
                  <div className="efm-inputs-row">
                    <div className="efm-field">
                      <label>From Emp</label>
                      <SearchableCodeSelect
                        value={rule.employees.from}
                        companyUuid={companyUuid}
                        placeholder="(first)"
                        fetchOptions={FieldConfigurationAPI.getEmployees}
                        {...EMPLOYEE_OPTION}
                        emptyMessage="No employees found"
                        disabled={readOnly}
                        onChange={(code) => updateRule(idx, "from", code)}
                      />
                    </div>
                    <div className="efm-field">
                      <label>To Emp</label>
                      <SearchableCodeSelect
                        value={rule.employees.to}
                        companyUuid={companyUuid}
                        placeholder="(last)"
                        fetchOptions={FieldConfigurationAPI.getEmployees}
                        {...EMPLOYEE_OPTION}
                        emptyMessage="No employees found"
                        disabled={readOnly}
                        onChange={(code) => updateRule(idx, "to", code)}
                      />
                    </div>
                  </div>
                  <div className="efm-inputs-row">
                    <div className="efm-field">
                      <label>From Location</label>
                      <input
                        value={rule.employees.from_location}
                        placeholder="(first)"
                        onChange={(e) =>
                          updateRule(idx, "from_location", e.target.value)
                        }
                      />
                    </div>
                    <div className="efm-field">
                      <label>To Location</label>
                      <input
                        value={rule.employees.to_location}
                        placeholder="(last)"
                        onChange={(e) =>
                          updateRule(idx, "to_location", e.target.value)
                        }
                      />
                    </div>
                  </div>
                  <div className="efm-status-row">
                    <div className="efm-field efm-field--status">
                      <label>Status</label>
                      <select
                        value={rule.employees.status || "active"}
                        onChange={(e) =>
                          updateRule(idx, "status", e.target.value)
                        }
                      >
                        <option value="active">Active</option>
                        <option value="inactive">Inactive</option>
                        <option value="">All</option>
                      </select>
                    </div>
                  </div>
                </div>

                {!readOnly && (
                  <button
                    className="jfm-delete"
                    onClick={() => removeRule(idx)}
                  >
                    <Trash2 size={16} />
                  </button>
                )}
              </div>
            ))}
          </fieldset>
        </div>

        {error && <div className="jfm-error">{error}</div>}

        <div className="jfm-footer">
          {readOnly ? (
            <div />
          ) : (
            <button
              className="jfm-add-row"
              onClick={() => setRules((prev) => [...prev, blankRule()])}
            >
              <Plus size={14} /> Add Row
            </button>
          )}
          <div className="jfm-footer-actions">
            {readOnly ? (
              <button className="jfm-cancel" onClick={onClose}>
                Close
              </button>
            ) : (
              <>
                <button
                  className="jfm-cancel"
                  onClick={onClose}
                  disabled={saving}
                >
                  Cancel
                </button>
                <button
                  className="jfm-save"
                  onClick={handleSave}
                  disabled={saving}
                >
                  {saving ? "Saving..." : "Save"}
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
